import { useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Play } from './SketchIconPack'
import { useTheme } from '../context/ThemeContext'
import Shuffle from './Shuffle'

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { theme } = useTheme()

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return
      const { clientX, clientY } = e
      const { innerWidth, innerHeight } = window
      const x = (clientX / innerWidth - 0.5) * 20
      const y = (clientY / innerHeight - 0.5) * 20

      const layers = heroRef.current.querySelectorAll<HTMLElement>('.parallax-layer')
      layers.forEach((layer, i) => {
        const speed = (i + 1) * 0.5
        layer.style.transform = `translate(${x * speed}px, ${y * speed}px)`
      })
    }

    window.addEventListener('mousemove', handleMouseMove)
    return () => window.removeEventListener('mousemove', handleMouseMove)
  }, [])



  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden -mt-[88px]"
    >
      {/* Hero sketch background image */}
      <div className="absolute inset-0 z-0">
        <img
          src={theme === 'dark' ? '/hero-sketch-dark.webp' : '/hero-sketch-light.webp'}
          alt="Abstract sketch background"
          className="w-full h-full object-cover opacity-20"
          style={{ filter: 'url(#sketch-filter)' }}
          fetchPriority="high"
          loading="eager"
          decoding="async"
          width={1920}
          height={1080}
        />
        {/* Gradient fade overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-bg-primary)] via-transparent to-[var(--color-bg-primary)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[var(--color-bg-primary)] via-transparent to-[var(--color-bg-primary)]" />
      </div>

      {/* Scan lines overlay */}
      <div className="absolute inset-0 scan-lines pointer-events-none z-[1]" />

      {/* Sharp geometric overlays */}
      <div className="absolute inset-0 sharp-overlay pointer-events-none z-[2]" />

      {/* Decorative sketch doodles */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.12, scale: 1 }}
        transition={{ delay: 1.8, duration: 0.4 }}
        className="absolute top-32 right-8 md:right-20 w-28 h-28 pointer-events-none z-[3]"
      >
        <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-[var(--color-text)] opacity-40">
          <circle cx="50" cy="50" r="30" strokeDasharray="5 5">
            <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="20s" repeatCount="indefinite" />
          </circle>
          <line x1="20" y1="20" x2="80" y2="80" strokeDasharray="4 4" />
          <line x1="80" y1="20" x2="20" y2="80" strokeDasharray="4 4" />
          <rect x="35" y="35" width="30" height="30" strokeDasharray="3 3">
            <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="-360 50 50" dur="15s" repeatCount="indefinite" />
          </rect>
        </svg>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.1, scale: 1 }}
        transition={{ delay: 2.0, duration: 0.4 }}
        className="absolute bottom-32 left-6 md:left-16 w-24 h-24 pointer-events-none z-[3]"
      >
        <svg viewBox="0 0 80 80" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-[var(--color-red)] opacity-50">
          <path d="M10 40 Q 25 10 40 40 Q 55 70 70 40" strokeDasharray="4 3" />
          <circle cx="40" cy="40" r="18" strokeDasharray="3 3">
            <animateTransform attributeName="transform" type="rotate" from="0 40 40" to="360 40 40" dur="25s" repeatCount="indefinite" />
          </circle>
        </svg>
      </motion.div>

      {/* Floating code brackets */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.06 }}
        transition={{ delay: 2.2 }}
        className="absolute top-1/4 left-10 text-6xl font-bold text-[var(--color-text)] pointer-events-none hidden md:block animate-float"
        style={{ animationDelay: '-2s' }}
      >
        {'{ }'}
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.06 }}
        transition={{ delay: 2.4 }}
        className="absolute bottom-1/4 right-10 text-5xl font-bold text-[var(--color-gold)] pointer-events-none hidden md:block animate-float"
        style={{ animationDelay: '-4s' }}
      >
        {'< />'}
      </motion.div>

      {/* Content */}
      <div className="relative z-10 container text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="flex justify-center mb-8"
        >
          <div className="section-label sketch-wobble">
            <div className="w-2 h-2 rounded-full bg-[var(--color-red)] animate-pulse-glow" />
            Premium Software Startup
          </div>
        </motion.div>

        {/* Title Line 1 */}
        <Shuffle
          text="WE DON'T BUILD WEBSITES"
          tag="h1"
          shuffleDirection="down"
          duration={0.4}
          stagger={0.03}
          shuffleTimes={1}
          className="text-[clamp(1.8rem,8vw,5.5rem)] font-bold font-[var(--font-heading)] leading-[1.1] tracking-tight text-[var(--color-text)] mb-2"
          triggerOnce={true}
          triggerOnHover={true}
          respectReducedMotion={true}
        />

        {/* Title Line 2 */}
        <Shuffle
          text="WE BUILD DIGITAL EMPIRES"
          tag="h2"
          shuffleDirection="down"
          duration={0.4}
          stagger={0.03}
          shuffleTimes={1}
          className="text-[clamp(1.8rem,8vw,5.5rem)] font-bold font-[var(--font-heading)] leading-[1.1] tracking-tight gradient-text mb-8"
          triggerOnce={true}
          triggerOnHover={true}
          respectReducedMotion={true}
        />

        {/* Subtitle */}
        <p
          className="text-lg md:text-xl text-[var(--color-text-muted)] max-w-2xl mx-auto mb-16 leading-relaxed"
        >
          We help ambitious brands scale by building high-performance web and mobile applications. Driven by design, engineered for growth.
        </p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 1.0 }}
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/pricing"
              className="btn-primary group ink-splash"
            >
              <span className="flex items-center gap-2">
                Start Project
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </a>
            <a
              href="/portfolio"
              className="btn-secondary group glitch-hover"
            >
              <Play size={16} className="text-[var(--color-gold)]" />
              Discover Our Work
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
